import React, { useEffect, useState } from 'react'
import { Outlet, useNavigate } from 'react-router-dom'
import HTMLReactParser from 'html-react-parser'
import '../styles/main.scss'
import '../styles/fonts.scss'
import '../styles/split-screen-v2.scss'

const TOTAL_IMAGES = 8

export interface SplitScreenContext {
  id: string
  title: string
  message: string
  contentSide?: 'left' | 'right'
  logoSrc?: string
  // Dark look of the new landing page (landing v2)
  variant?: 'default' | 'v2'
}

interface SplitScreenLayoutProps {
  context: SplitScreenContext
}

export const SplitScreenLayout = ({
  context: { id, title: initialTitle, message: initialMessage, contentSide = 'right', logoSrc = '/images/logo.png', variant = 'default' },
}: SplitScreenLayoutProps) => {
  const navigate = useNavigate()

  const [title, setTitle] = useState(initialTitle)
  const [message, setMessage] = useState(initialMessage)
  const [rotationCount, setRotationCount] = useState(() => {
    const savedCount = localStorage.getItem('splitScreenImageRotation')
    return savedCount ? parseInt(savedCount, 10) : 0
  })

  // Update state when context props change
  useEffect(() => {
    setTitle(initialTitle)
    setMessage(initialMessage)
  }, [initialTitle, initialMessage])

  // Handle image rotation
  useEffect(() => {
    const newCount = rotationCount + 1
    setRotationCount(newCount)
    localStorage.setItem('splitScreenImageRotation', newCount.toString())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Always use light mode for split screen pages
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', 'light')
  }, [])

  const imageIndex = rotationCount % TOTAL_IMAGES
  const imagePath = `/images/split-screen/split-screen-${imageIndex}.jpg`

  return (
    <div
      id={id}
      className={`split-screen-container ${contentSide === 'left' ? 'content-left' : 'content-right'}${variant === 'v2' ? ' split-screen-v2' : ''}`}
    >
      <div
        className="split-screen-image"
        style={{
          backgroundImage: `url(${imagePath})`,
        }}
      />
      <div className="split-screen-content">
        <div className="content-wrapper">
          <img
            className="split-screen-logo"
            src={logoSrc}
            alt="Split Screen"
            onClick={() => navigate('/')}
          />
          <h3 className="display-4">{HTMLReactParser(title)}</h3>
          <p className="muted mb-4">{HTMLReactParser(message)}</p>
          <main>
            <Outlet context={{ setTitle, setMessage }} />
          </main>
        </div>
      </div>
    </div>
  )
}

export default SplitScreenLayout
